import React from 'react';
import { format } from 'date-fns';
import { formatTime, getCheckoutTime } from '../../../utils/job-utils';
import JobBadges from '../indicators/JobBadges';
import { 
  AreaIndicator
} from '../../../components/job-indicators';
import DueByDisplay from '../../../components/DueByDisplay';
import { getPropertyManagementLabel } from '../../../utils/propertyManagers';

export default function JobCardUnassigned({ 
  job, 
  selectedJobs,
  setSelectedJobs,
  setEditingJob,
  index = 0
}) {
  if (!job) return null;
  
  const isSelected = selectedJobs?.includes(job.id);
  
  const handleSelect = (selected) => {
    if (selected) {
      setSelectedJobs([...selectedJobs, job.id]);
    } else {
      setSelectedJobs(selectedJobs.filter(id => id !== job.id));
    }
  };
  
  const handleEdit = (e) => {
    e.stopPropagation(); // Prevent card click from triggering
    setEditingJob(job);
  };

  const handleCardClick = () => {
    handleSelect(!isSelected);
  };
  
  // Helper function to get checkout date for display
  // Only shows date for CHECKIN-based jobs (like pre-arrival inspections)
  // because the previous checkout date might be different from the job's scheduled date
  const getCheckoutDate = (job) => {
    // Only show date for CHECKIN-based jobs
    if (job.serviceType?.timeBase !== 'CHECKIN') {
      return null;
    }
    
    // For CHECKIN-based jobs, use previous reservation checkout
    if (job.previousReservation?.check_out) {
      try {
        const checkoutDate = new Date(job.previousReservation.check_out);
        const month = checkoutDate.getMonth() + 1;
        const day = checkoutDate.getDate();
        return `${month}/${day}`;
      } catch (error) {
        return null;
      }
    }
    
    return null;
  };
  
  // Check if this is a flip job
  const isFlipJob = job.isFlip;
  const shouldUseServiceTypeColor = job.serviceType?.color;
  
  // Service type color style for direct CSS if needed
  const serviceTypeColorStyle = shouldUseServiceTypeColor 
    ? { 
        '--service-type-color': job.serviceType.color,
        borderLeftColor: job.serviceType.color,
        ...(isFlipJob && {
          borderTopColor: '#ef4444',
          borderRightColor: '#ef4444', 
          borderBottomColor: '#ef4444',
          borderTopWidth: '2px',
          borderRightWidth: '2px',
          borderBottomWidth: '2px',
          borderTopStyle: 'solid',
          borderRightStyle: 'solid',
          borderBottomStyle: 'solid'
        })
      } 
    : isFlipJob 
      ? {
          borderColor: '#ef4444',
          borderWidth: '2px',
          borderStyle: 'solid'
        }
      : {};

  // Determine background color based on selection - more blue when selected
  const bgColorClass = isSelected 
    ? 'bg-blue-100' 
    : 'bg-gray-50';

  // Apply consistent thick border styling - always use service type color when available
  const finalBorderClass = shouldUseServiceTypeColor
    ? isFlipJob 
      ? 'border-l-[24px] relative border-2 border-red-500 !border-red-500'  // Service type color + red outer border for flip
      : 'border-l-[24px] relative border border-gray-200' // Service type color + gray border
    : isFlipJob
      ? 'border-l-[24px] border-2 border-red-500 !border-red-500'           // Gray left border + red outer border for flip
      : 'border-l-[24px] border border-gray-200';         // Gray left border + gray border
  
  return (
    <div 
      className={`flex pl-1 p-4 pb-4 pt-4 my-2 rounded-lg shadow ${finalBorderClass} ${bgColorClass} cursor-pointer relative hover:shadow-md transition-shadow`}
      style={serviceTypeColorStyle}
      onClick={handleCardClick}
    >
      <div className="flex flex-col justify-between items-center flex-1 w-6 mr-1">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => {}} // No-op since card click handles this
          className="h-4 w-4 text-blue-600 rounded mt-1 pointer-events-none"
        />
        <span className="flex items-center justify-center h-5 w-5 text-xs text-gray-500 font-mono bg-gray-100 rounded-full">
          {index + 1}
        </span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 w-full">
        {/* Left Column */}
        <div className="flex flex-col">
          <div className="flex items-start gap-2">
            

            <div>
              {/* Job title at top taking up 50% width */}
              <h4 className="font-medium text-gray-900 whitespace-nowrap mb-1 w-full max-w-[50%]">{job.property?.name || 'Unknown Property'}</h4>
              <p className="text-xs text-gray-500 mb-1">
                {job.scheduledDate ? format(new Date(job.scheduledDate), 'MMM d, yyyy') : 'Unknown Date'}
                {getPropertyManagementLabel(job.property) && (
                  <span className="text-gray-400">
                    {' • PM: '}
                    <span className="text-gray-600">{getPropertyManagementLabel(job.property)}</span>
                  </span>
                )}
              </p>
              {/* Only show Guest Checkout for non-custom jobs */}
              {job.serviceType?.timeBase !== 'CUSTOM' && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    <div className="text-sm mr-1 font-medium text-gray-600">
                      Guest Checkout:
                    </div>
                    {job.hasTimeOverride ? (
                      <div className="flex flex-col text-sm">
                        {getCheckoutTime(job) && (
                          <span className="line-through text-gray-400">
                            {formatTime(getCheckoutTime(job))}
                            {getCheckoutDate(job) && <span className="ml-1">on {getCheckoutDate(job)}</span>}
                          </span>
                        )}
                        <span className="flex items-center text-sky-600 font-semibold">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-0.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                          </svg>
                          {formatTime(job.startTime)}
                          {getCheckoutDate(job) && <span className="ml-1">on {getCheckoutDate(job)}</span>}
                        </span>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-800 font-semibold">
                        {formatTime(getCheckoutTime(job))}
                        {getCheckoutDate(job) && <span className="text-gray-600 ml-1">on {getCheckoutDate(job)}</span>}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Show Start Time for custom jobs */}
              {job.serviceType?.timeBase === 'CUSTOM' && job.startTime && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    <div className="text-sm mr-1 font-medium text-gray-600">
                      Start Time:
                    </div>
                    <div className="text-sm text-gray-800 font-semibold">{formatTime(job.startTime)}</div>
                  </div>
                </div>
              )}
              {/* Show "Due by" for all job types */}
              <div className="text-sm mt-1 bg-blue-50 p-1 rounded">
                <span className="inline-flex items-center flex-wrap">
                  <svg className="h-4 w-4 mr-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-gray-600 font-medium mr-1">Due by:</span>
                  <span className="text-blue-700 font-semibold"><DueByDisplay job={job} /></span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col justify-between items-between">
                    {/* Indicators */}
          <div className="mb-2 flex flex-row-reverse gap-1 whitespace-nowrap justify-start flex-wrap">
            <JobBadges job={job} allJobs={[]} />
          </div>
          {/* Bottom area and edit button */}
          <div className="flex justify-end items-center gap-2">
            {job.property?.area && <AreaIndicator area={job.property.area} />}
            
            <button
              onClick={handleEdit}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 